import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { MouseEvent, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AnimatePresence } from "framer-motion";

import BackButton from "@/components/BackButton";
import Button from "@/components/Button";
import { PageHeading } from "@/components/Layout/Parts";
import Text from "@/components/Text";
import ContentSpinner from "@/components/ContentSpinner";
import { api } from "@/utils/api";
import useAlerts from "@/hooks/useAlerts";
import InfoCard from "./components/InfoCard";
import ContentCard from "./components/ContentCard";
import { FBImageDistributionResource } from "@/types/resources";
import { ImageDistributionStatus } from "@/utils/enums";
import PostModal from "../ImageDistribution/components/PostModal";

const HeadingRow = styled.div`
  display: flex;
  justify-content: space-between;
  button {
    flex-shrink: 0;
  }
`;

const StyledPageHeading = styled(PageHeading)`
  flex: 1;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 16px;
  margin-left: 30px;
`;

const EditButton = styled(Button)`
  border-color: ${({ theme }) => theme.colors.lightGray};
  background: transparent;
  padding: 0 32px;
  &:hover {
    border-color: rgba(34, 34, 34, 0.25);
  }
`;

const DeleteButton = styled(Button)`
  border-color: ${({ theme }) => theme.colors.lightGray};
  background: transparent;
  padding: 0 32px;
  &:hover {
    border-color: ${({ theme }) => theme.colors.error};
    color: ${({ theme }) => theme.colors.error};
  }
`;

const ImageDistributionSingle = () => {
  const [showEditModal, setShowEditModal] = useState(false);

  const { id } = useParams();

  const navigate = useNavigate();

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const { isLoading, data: post } = useQuery(
    ["fb_image_distribution", { id }],
    () =>
      api.get<never, FBImageDistributionResource>(
        `/v1/distribution/image-posts/${id}`
      ),
    {
      staleTime: 60 * 1000,
      retry: 2,
      onError: (err) => {
        if ((err as any)?.response?.status === 404) {
          return navigate("/404", {
            replace: true,
          });
        }
        alerts.error();
      },
    }
  );

  if (isLoading || !post) {
    return <ContentSpinner />;
  }

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirm = await alerts.confirm(
        `Are you sure you want to delete this post?`
      );

      if (!confirm) return;

      await api.delete(`/v1/distribution/image-posts/${post.id}`);

      navigate("/general/image-distribution");

      await queryClient.invalidateQueries(["fb_image_distribution"]);

      alerts.success("Post was successfully deleted");
    } catch (e) {
      alerts.error();
    }
  };

  return (
    <>
      <Text $type="caption" $color="gray">
        EDITORIAL
      </Text>
      <HeadingRow>
        <StyledPageHeading>{post.name}</StyledPageHeading>
        <ActionButtons>
          {post.status === ImageDistributionStatus.DRAFT && (
            <EditButton
              color="secondary"
              onClick={() => setShowEditModal(true)}
            >
              Edit
            </EditButton>
          )}
          <DeleteButton color="secondary" onClick={onDelete}>
            Delete
          </DeleteButton>
        </ActionButtons>
      </HeadingRow>
      <BackButton />
      <InfoCard post={post} />
      <ContentCard post={post} />
      <AnimatePresence>
        {showEditModal && (
          <PostModal onClose={() => setShowEditModal(false)} post={post} />
        )}
      </AnimatePresence>
    </>
  );
};

export default ImageDistributionSingle;
